import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiResponse } from "../../types/responseType";
import { TicketDetails } from "../../types/helpDeskType";
import helpDeskApis from "../../api/helpDeskApis";
import { PaginatedResponse } from "../../types/paginationType";

interface HelpDeskState {
  tickets: TicketDetails[];
  loading: boolean;
  error: string | null;
  pagination: {
    totalPages: number;
    totalElements: number;
    page: number;
    size: number;
  };
}

const initialState: HelpDeskState = {
  tickets: [],
  loading: false,
  error: null,
  pagination: {
    totalPages: 0,
    totalElements: 0,
    page: 0,
    size: 10,
  },
};

export const fetchTickets = createAsyncThunk<
  ApiResponse<PaginatedResponse<TicketDetails>>,
  Record<string, string>,
  { rejectValue: string }
>("helpDesk/fetchTickets", async (params, { rejectWithValue }) => {
  try {
    return await helpDeskApis.fetchTickets(params);
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.error || "Failed to fetch tickets"
    );
  }
});

const helpDeskSlice = createSlice({
  name: "helpDesk",
  initialState,
  reducers: {
    clearTickets(state) {
      state.tickets = [];
      state.error = null;
    },
    setPage(state, action) {
      state.pagination.page = action.payload;
    },
    setPageSize(state, action) {
      state.pagination.size = action.payload;
      state.pagination.page = 0; 
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload.data;
        state.tickets = data.content;
        state.pagination = {
          totalPages: data.totalPages,
          totalElements: data.totalElements,
          page: data.number,
          size: data.size,
        };
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      });
  },
});

export const { clearTickets,setPage,setPageSize } = helpDeskSlice.actions;
export default helpDeskSlice.reducer;
